import { PAY_CYCLE_DAYS, addDays } from './roadmapCalcs'
import type { PayFrequency } from '../types/roadmap'

export interface Pillar {
  /** 1, 2 or 3. */
  index: number
  /** Amount to save during this stage. */
  amount: number
  /** Running total once this stage is finished. */
  cumulative: number
  /** Share of the whole gap, for the segmented bar. */
  share: number
  /** Projected date, or null when no per-pay amount was given. */
  date: Date | null
}

/**
 * Split what is left to save into three staged milestones on a 1 : 2 : 2 ratio.
 *
 * Deliberately not an even split — the first stage is the smallest so there is
 * an early, achievable win, which is what keeps someone saving. Stages one and
 * two round to the nearest $500 for readability; stage three takes the exact
 * remainder so the three always sum precisely to the gap.
 *
 * Measured on the REMAINING GAP, not the total goal: money already saved is
 * banked, and only money still to come has a meaningful date attached.
 */
export function calculatePillars(
  savingsGap: number,
  perPay: number,
  payFrequency: PayFrequency | null,
  createdOn: Date = new Date(),
): Pillar[] {
  if (savingsGap <= 0) return []

  const round500 = (value: number) => Math.round(value / 500) * 500

  const first = Math.min(round500(savingsGap / 5), savingsGap)
  const second = Math.min(round500((savingsGap * 2) / 5), savingsGap - first)
  const third = savingsGap - first - second

  const amounts = [first, second, third]

  let running = 0
  return amounts.map((amount, position) => {
    running += amount
    const cumulative = running

    let date: Date | null = null
    if (perPay > 0 && payFrequency) {
      const pays = Math.ceil(cumulative / perPay)
      date = addDays(createdOn, pays * PAY_CYCLE_DAYS[payFrequency])
    }

    return {
      index: position + 1,
      amount,
      cumulative,
      share: amount / savingsGap,
      date,
    }
  })
}
